// src/editor/HandleInsertHeading.js
import { TextSelection } from "prosemirror-state";
import safeUuid from "../utils/safeUuid";

/**
 * Inserts a new table row with proper metadata structure matching C# GenerateTabularTipTap.
 * Automatically inherits segmentType and segmentName from the current row.
 *
 * Call like:
 * handleInsertHeading(editor, {
 *   fieldId: "other_deductions",
 *   headingType: "OtherDeductions",
 *   headingText: "OTHER DEDUCTIONS FROM PREMIUM",
 *   contentText: "None",
 *   editableBy: ["insurer"],
 *   insertAfter: true,
 *   fontSize: "12pt"
 * })
 */
export function handleInsertHeading(
  editor,
  {
    id = safeUuid(),
    fieldId = "",
    headingType = "",
    headingText = "",
    contentText = "None",
    editableBy = ["insurer"],
    adminBy = [],
    insertAfter = true, // set false to insert before current row
    fontSize = null, // optional font size (e.g., "12pt", "14px", "1rem")
  } = {}
) {
  if (!editor) return false;

  const { state, view } = editor;
  const { schema, tr, selection } = state;
  const $from = selection.$from;

  // Find the closest tableRow the selection is in
  let rowPos = null;
  let currentRowNode = null;
  for (let d = $from.depth; d >= 0; d--) {
    const node = $from.node(d);
    if (node.type && node.type.name === "tableRow") {
      currentRowNode = node;
      rowPos = $from.before(d); // position before the row
      break;
    }
  }
  if (rowPos == null || !currentRowNode) {
    // Not inside a table — bail
    return false;
  }

  // Inherit segmentType and segmentName from the current row's attributes
  const currentRowAttrs = currentRowNode.attrs || {};
  const segmentType = currentRowAttrs.segmentType || "";
  const segmentName = currentRowAttrs.segmentName || "";

  const colCount = Math.max(2, currentRowNode.childCount || 2);

  const makeParagraph = (text) => {
    // Create text node with optional font size mark
    let textNode = schema.text(text ?? "");
    if (fontSize && schema.marks.textStyle) {
      try {
        const fontSizeMark = schema.marks.textStyle.create({ fontSize });
        textNode = textNode.mark([fontSizeMark]);
      } catch (e) {
        console.warn("Failed to apply fontSize mark:", e);
      }
    }
    return schema.nodes.paragraph.create(null, textNode);
  };

  const parseRichContent = (content) => {
    // Try to parse as Tiptap JSON (rich text)
    try {
      if (typeof content === 'string' && content.trim().startsWith('{')) {
        const jsonContent = JSON.parse(content);

        // Check if it's a valid Tiptap document structure
        if (jsonContent.type === 'doc' && Array.isArray(jsonContent.content)) {
          // Convert Tiptap JSON nodes to ProseMirror nodes
          const nodes = jsonContent.content.map(nodeData => {
            return schema.nodeFromJSON(nodeData);
          });
          return nodes.length > 0 ? nodes : [makeParagraph("")];
        }
      }
    } catch (e) {
      // Not valid JSON or not a Tiptap document, treat as plain text
    }

    // Fallback to plain text
    return [makeParagraph(content || "")];
  };

  // first cell = heading, second = content, any remaining = blank (role "output" on 3rd if you use it)
  const cells = [];
  for (let i = 0; i < colCount; i++) {
    if (i === 0) {
      cells.push(
        schema.nodes.tableCell.create({ role: "heading" }, makeParagraph(headingText))
      );
    } else if (i === 1) {
      // Parse content - could be rich text (Tiptap JSON) or plain text
      const contentNodes = parseRichContent(contentText);
      cells.push(
        schema.nodes.tableCell.create({ role: "content" }, contentNodes)
      );
    } else {
      const role = i === 2 ? "output" : undefined;
      cells.push(schema.nodes.tableCell.create(role ? { role } : {}, makeParagraph("")));
    }
  }

  // derive fieldId if empty (matches C# logic: content.headingType?.ToLower())
  const derivedFieldId =
    fieldId ||
    (headingType
      ? String(headingType).replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "").toLowerCase()
      : "");

  // Build the row with attrs matching C# GenerateTabularTipTap structure
  const rowAttrs = {
    id,
    fieldId: derivedFieldId,
    headingType,
    codifyStatus: "idle" // Initialize new rows with idle status
  };

  // Add segmentType and segmentName inherited from current row (matches C# conditional logic)
  if (segmentType) {
    rowAttrs.segmentType = segmentType;
  }
  if (segmentName) {
    rowAttrs.segmentName = segmentName;
  }
  if (editableBy && editableBy.length > 0) {
    rowAttrs.editableBy = editableBy;
  }
  if (adminBy && adminBy.length > 0) {
    rowAttrs.adminBy = adminBy;
  }

  const newRow = schema.nodes.tableRow.create(rowAttrs, cells);

  // Compute insert position (before/after current row)
  const insertPos = insertAfter ? rowPos + currentRowNode.nodeSize : rowPos;

  const nextTr = tr.insert(insertPos, newRow);

  // (optional) place the cursor into the new content cell (second cell)
  try {
    // The row starts at insertPos; step into its second cell
    let offset = insertPos + 1; // inside the new tableRow
    for (let i = 0; i < newRow.childCount; i++) {
      const cell = newRow.child(i);
      if (i === 1) {
        // move inside paragraph of the content cell
        const pos = Math.min(offset + 2, nextTr.doc.content.size);
        nextTr.setSelection(TextSelection.create(nextTr.doc, pos));
        break;
      }
      offset += cell.nodeSize;
    }
  } catch {
    // ignore selection errors; still insert the row
  }

  view.dispatch(nextTr.scrollIntoView());
  return true;
}

// Keep your original name export for compatibility
export const HandleInsertHeading = handleInsertHeading;
